'use client';

/**
 * Conversation Context and Hook
 *
 * Manages the current conversation state for the chat-based agent lifecycle.
 * Creates a conversation on first message and tracks all executions within it.
 * Supports multiple conversations with switching.
 */

import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { apiClient } from './apiClient';
import { getIdentity } from './config/services';
import type {
  ConversationResponse,
  ConversationWithExecutions,
  ConversationListResponse,
  ConversationMessage,
  LifecycleMode,
  PlatformAgentDefinition,
  PlatformConversationResponse,
} from '../types';
import type { ThreadMessageLike } from '@assistant-ui/react';
import type { ExecutionEvent } from '@/components/assistant-ui/execution-log';

// JSON-compatible types for tool arguments (matches assistant-ui's ReadonlyJSONObject)
type JSONValue = string | number | boolean | null | JSONObject | JSONArray;
type JSONObject = { readonly [key: string]: JSONValue };
type JSONArray = readonly JSONValue[];

type TextPart = { type: 'text'; text: string };
type ToolCallPart = {
  type: 'tool-call';
  toolCallId: string;
  toolName: string;
  args: JSONObject;
  argsText: string;
  result?: unknown;
  isError?: boolean;
};
type AssistantPart = TextPart | ToolCallPart;

/**
 * Extract text from various content formats returned by backend
 * Handles: string, { text: string }, { content: [{ text, type }] }
 */
function extractTextFromContent(content: unknown): string {
  if (typeof content === 'string') return content;
  if (content && typeof content === 'object') {
    const obj = content as { content?: Array<{ text?: string; type?: string }> | string; text?: string };
    // Handle nested content array: { content: [{ text: "...", type: "text" }] }
    if (Array.isArray(obj.content)) {
      return obj.content
        .filter(p => p.type === 'text' && p.text)
        .map(p => p.text || '')
        .join('\n');
    }
    // Handle direct content string: { content: "..." }
    if (typeof obj.content === 'string') return obj.content;
    // Handle text property: { text: "..." }
    if (obj.text) return obj.text;
  }
  return '';
}

// Metrics accumulated for an assistant message (exported for use in components)
export interface MessageMetadata {
  input_tokens: number;
  output_tokens: number;
  cost_usd: number;
  duration_ms: number;
  model?: string;
}

// Result of message conversion - includes messages, metadata, and flat execution events
export interface ConversionResult {
  messages: ThreadMessageLike[];
  metadata: Map<string, MessageMetadata>;  // messageId -> metadata
  events: ExecutionEvent[];  // Flat array of execution events for ExecutionLog
  isLastExecutionActive: boolean;  // True if the last execution has no completed_at
}

/**
 * Convert backend ConversationMessage[] to ThreadMessageLike[] for assistant-ui
 * Groups messages by role and reconstructs the chat format
 * Also generates execution events for historical display with proper subagent nesting
 * Returns messages, metadata map, and flat events array for ExecutionLog
 */
function convertMessagesToThreadFormat(messages: ConversationMessage[]): ConversionResult {
  const threadMessages: ThreadMessageLike[] = [];
  const metadataMap = new Map<string, MessageMetadata>();
  const allEvents: ExecutionEvent[] = [];  // Flat array of all events

  let currentAssistantParts: AssistantPart[] = [];
  let currentAssistantId: string | null = null;
  let currentMetrics: MessageMetadata = { input_tokens: 0, output_tokens: 0, cost_usd: 0, duration_ms: 0 };

  // Sort messages by created_at timestamp (most reliable for ordering across executions)
  const sorted = [...messages].sort((a, b) => {
    const timeA = new Date(a.created_at).getTime();
    const timeB = new Date(b.created_at).getTime();
    if (timeA !== timeB) return timeA - timeB;
    // Secondary sort by sequence if timestamps are equal
    return a.message_sequence - b.message_sequence;
  });

  // Track subagent relationships for event generation
  const seenSubagents = new Set<string>();
  // Track subagents that have explicit completed events from backend (don't generate zeros for these)
  const subagentsWithExplicitCompleted = new Set<string>();
  // Track the last execution ID for generating agent_completed events
  let lastExecutionId: string | null = null;
  let lastTimestamp = 0;
  // Track if the last execution is still active (no completed_at)
  let lastExecutionIsActive = false;

  // Helper to flush pending assistant message with accumulated metrics
  const flushAssistantMessage = () => {
    if (currentAssistantId && currentAssistantParts.length > 0) {
      threadMessages.push({
        id: currentAssistantId,
        role: 'assistant',
        content: currentAssistantParts,
        status: { type: 'complete', reason: 'stop' },
      });
      // Store metadata separately for lookup by message ID
      metadataMap.set(currentAssistantId, { ...currentMetrics });

      // Generate agent_completed event for this message (so "Done" shows in execution log)
      if (lastExecutionId) {
        // First, generate subagent_completed events for any subagents we saw
        // SKIP subagents that have explicit completed events from backend
        for (const subagentId of seenSubagents) {
          if (subagentId !== lastExecutionId && !subagentsWithExplicitCompleted.has(subagentId)) {
            allEvents.push({
              type: 'subagent_completed',
              execution_id: subagentId,
              parent_execution_id: lastExecutionId,
              messageId: currentAssistantId,
              timestamp: lastTimestamp + 1,
              duration_ms: 0,
              total_input_tokens: 0,
              total_output_tokens: 0,
              cost_usd: 0,
            });
          }
        }

        // Then generate execution_completed for the root execution
        allEvents.push({
          type: 'execution_completed',
          execution_id: lastExecutionId,
          messageId: currentAssistantId,
          timestamp: lastTimestamp + 2,
          duration_ms: currentMetrics.duration_ms,
          total_input_tokens: currentMetrics.input_tokens,
          total_output_tokens: currentMetrics.output_tokens,
          cost_usd: currentMetrics.cost_usd,
        });
      }

      currentAssistantParts = [];
      currentAssistantId = null;
      currentMetrics = { input_tokens: 0, output_tokens: 0, cost_usd: 0, duration_ms: 0 };
      seenSubagents.clear();
      lastExecutionId = null;
    }
  };

  for (const msg of sorted) {
    console.log('🔄 Processing message:', msg.role, msg.content_type, msg.message_id, 'exec:', msg.agent_execution_id, 'parent:', msg.parent_agent_id);

    if (msg.role === 'user' && msg.content_type === 'text') {
      // Flush any pending assistant message
      flushAssistantMessage();

      // Add user message
      const content = extractTextFromContent(msg.content);
      console.log('👤 Adding user message:', content.slice(0, 50));
      threadMessages.push({
        id: msg.message_id,
        role: 'user',
        content: content,
        createdAt: new Date(msg.created_at),
      });
    } else if (msg.role === 'user') {
      // User message with non-text content type - still try to extract
      console.log('👤 User message with content_type:', msg.content_type);
      flushAssistantMessage();
      const content = extractTextFromContent(msg.content);
      if (content) {
        threadMessages.push({
          id: msg.message_id,
          role: 'user',
          content: content,
          createdAt: new Date(msg.created_at),
        });
      }
    } else if (msg.content_type === 'subagent_started') {
      // Explicit subagent started event from backend (check BEFORE role=assistant)
      const timestamp = new Date(msg.created_at).getTime();
      const content = msg.content as Record<string, unknown>;
      const messageId = currentAssistantId || msg.message_id;

      seenSubagents.add(msg.agent_execution_id);

      allEvents.push({
        type: 'subagent_started',
        execution_id: msg.agent_execution_id,
        parent_execution_id: msg.parent_agent_id || lastExecutionId || '',
        messageId,
        timestamp,
        agent_type: (content?.agent_type as string) || 'subagent',
        goal: (content?.goal as string) || '',
      });
    } else if (msg.content_type === 'subagent_completed') {
      // Explicit subagent completed event from backend - contains real metrics (check BEFORE role=assistant)
      const timestamp = new Date(msg.created_at).getTime();
      const content = msg.content as Record<string, unknown>;
      const messageId = currentAssistantId || msg.message_id;

      subagentsWithExplicitCompleted.add(msg.agent_execution_id);
      seenSubagents.add(msg.agent_execution_id);

      allEvents.push({
        type: 'subagent_completed',
        execution_id: msg.agent_execution_id,
        parent_execution_id: msg.parent_agent_id || lastExecutionId || '',
        messageId,
        timestamp,
        duration_ms: (content?.duration_ms as number) || (msg.duration_seconds ? msg.duration_seconds * 1000 : 0),
        total_input_tokens: (content?.total_input_tokens as number) || msg.input_tokens || 0,
        total_output_tokens: (content?.total_output_tokens as number) || msg.output_tokens || 0,
        cost_usd: (content?.cost_usd as number) || msg.cost_usd || 0,
      });
    } else if (msg.role === 'assistant') {
      if (!currentAssistantId) {
        currentAssistantId = msg.message_id;
      }

      const timestamp = new Date(msg.created_at).getTime();
      const messageId = currentAssistantId;

      // Track execution ID and timestamp for agent_completed event generation
      if (!lastExecutionId && msg.agent_execution_id) {
        lastExecutionId = msg.agent_execution_id;
      }
      lastTimestamp = Math.max(lastTimestamp, timestamp);

      // Track if execution is still active (no completed_at means still running)
      // Reset for each assistant block - will be final value for last assistant message
      lastExecutionIsActive = !msg.completed_at;
      console.log('🔄 Assistant message completed_at check:', msg.message_id, 'completed_at:', msg.completed_at, 'isActive:', lastExecutionIsActive);

      // Track subagent start (when we first see a message from a subagent)
      if (msg.parent_agent_id && !seenSubagents.has(msg.agent_execution_id)) {
        seenSubagents.add(msg.agent_execution_id);
        allEvents.push({
          type: 'subagent_started',
          execution_id: msg.agent_execution_id,
          parent_execution_id: msg.parent_agent_id,
          messageId,
          timestamp,
          agent_type: 'subagent', // Backend doesn't provide this for historical
          goal: '', // Would need to extract from spawning tool call
        });
      }

      // Accumulate metrics from this message
      if (msg.input_tokens) currentMetrics.input_tokens += msg.input_tokens;
      if (msg.output_tokens) currentMetrics.output_tokens += msg.output_tokens;
      if (msg.cost_usd) currentMetrics.cost_usd += msg.cost_usd;
      if (msg.duration_seconds) currentMetrics.duration_ms += msg.duration_seconds * 1000;
      if (msg.model) currentMetrics.model = msg.model;

      if (msg.content_type === 'text') {
        const text = extractTextFromContent(msg.content);
        if (text) {
          currentAssistantParts.push({ type: 'text', text });
          // Add LLM response event
          allEvents.push({
            type: 'llm_end',
            execution_id: msg.agent_execution_id,
            messageId,
            timestamp,
            content: [{ type: 'text', text }],
            input_tokens: msg.input_tokens || 0,
            output_tokens: msg.output_tokens || 0,
            cost_usd: msg.cost_usd || 0,
            duration_ms: msg.duration_seconds ? msg.duration_seconds * 1000 : 0,
            model: msg.model || '',
            stop_reason: '',
          });
        }
      } else if (msg.content_type === 'tool_call' && msg.tool_name) {
        currentAssistantParts.push({
          type: 'tool-call',
          toolCallId: msg.message_id,
          toolName: msg.tool_name,
          args: (msg.tool_input || {}) as JSONObject,
          argsText: JSON.stringify(msg.tool_input || {}, null, 2),
          result: msg.tool_output,
          isError: msg.tool_status === 'error',
        });
        // Add tool call started event
        allEvents.push({
          type: 'tool_call_started',
          execution_id: msg.agent_execution_id,
          messageId,
          timestamp,
          tool_use_id: msg.message_id,
          tool_name: msg.tool_name,
          tool_input: msg.tool_input || {},
          is_async: false,
        });
      }
    } else if (msg.role === 'tool' && msg.content_type === 'tool_call_completed') {
      // Tool results - find matching tool call and update it
      const toolCall = currentAssistantParts.find(
        p => p.type === 'tool-call' && p.toolName === msg.tool_name
      );
      if (toolCall && toolCall.type === 'tool-call') {
        toolCall.result = msg.tool_output || msg.content;
        toolCall.isError = msg.tool_status === 'error';
      }

      // Update lastTimestamp to include tool result time
      const toolTimestamp = new Date(msg.created_at).getTime();
      lastTimestamp = Math.max(lastTimestamp, toolTimestamp);

      // Track subagent for tool messages too (same logic as assistant messages)
      if (msg.parent_agent_id && msg.agent_execution_id && !seenSubagents.has(msg.agent_execution_id) && currentAssistantId) {
        seenSubagents.add(msg.agent_execution_id);
        allEvents.push({
          type: 'subagent_started',
          execution_id: msg.agent_execution_id,
          parent_execution_id: msg.parent_agent_id,
          messageId: currentAssistantId,
          timestamp: toolTimestamp,
          agent_type: 'subagent', // Backend doesn't provide this for historical
          goal: '', // Would need to extract from spawning tool call
        });
      }

      // Add tool result event
      if (currentAssistantId) {
        allEvents.push({
          type: 'tool_call_completed',
          execution_id: msg.agent_execution_id,
          messageId: currentAssistantId,
          timestamp: toolTimestamp,
          tool_use_id: msg.message_id,
          tool_name: msg.tool_name || '',
          result: msg.tool_output || msg.content,
          is_error: msg.tool_status === 'error',
          duration_ms: msg.duration_seconds ? msg.duration_seconds * 1000 : 0,
        });
      }
    } else if (msg.content_type === 'tools_finder_started') {
      // Tools finder started event
      const timestamp = new Date(msg.created_at).getTime();
      const content = msg.content as Record<string, unknown>;
      if (currentAssistantId) {
        allEvents.push({
          type: 'tools_finder_started',
          execution_id: msg.agent_execution_id,
          messageId: currentAssistantId,
          timestamp,
          unavailable_tools: (content?.unavailable_tools as string[]) || [],
          count: (content?.count as number) || 0,
        });
      }
    } else if (msg.content_type === 'tools_finder_completed') {
      // Tools finder completed event
      const timestamp = new Date(msg.created_at).getTime();
      const content = msg.content as Record<string, unknown>;
      if (currentAssistantId) {
        allEvents.push({
          type: 'tools_finder_completed',
          execution_id: msg.agent_execution_id,
          messageId: currentAssistantId,
          timestamp,
          total_tools: (content?.total_tools as number) || 0,
          alternatives_found: (content?.alternatives_found as number) || 0,
          results: (content?.results as Record<string, unknown>) || {},
        });
      }
    } else if (msg.content_type === 'llm_start') {
      // LLM start event from subagent - indicates LLM call beginning
      const timestamp = new Date(msg.created_at).getTime();
      const content = msg.content as Record<string, unknown>;
      const messageId = currentAssistantId || msg.message_id;
      console.log('📥 [HISTORY] llm_start event:', { agent_execution_id: msg.agent_execution_id, agent_name: content?.agent_name });
      allEvents.push({
        type: 'llm_start',
        execution_id: msg.agent_execution_id,
        messageId,
        timestamp,
        agent_name: (content?.agent_name as string) || '',
      });
    } else if (msg.content_type === 'llm_end') {
      // LLM end event from subagent - indicates LLM call completed with token stats
      const timestamp = new Date(msg.created_at).getTime();
      const content = msg.content as Record<string, unknown>;
      const messageId = currentAssistantId || msg.message_id;
      console.log('📥 [HISTORY] llm_end event:', { agent_execution_id: msg.agent_execution_id, agent_name: content?.agent_name, tokens: content?.input_tokens });
      allEvents.push({
        type: 'llm_end',
        execution_id: msg.agent_execution_id,
        messageId,
        timestamp,
        agent_name: (content?.agent_name as string) || '',
        input_tokens: (content?.input_tokens as number) || msg.input_tokens || 0,
        output_tokens: (content?.output_tokens as number) || msg.output_tokens || 0,
        cost_usd: (content?.cost_usd as number) || msg.cost_usd || 0,
      });
    } else if (msg.content_type === 'agent_start') {
      // Agent start event from subagent - indicates subagent agent beginning
      const timestamp = new Date(msg.created_at).getTime();
      const content = msg.content as Record<string, unknown>;
      const messageId = currentAssistantId || msg.message_id;
      console.log('📥 [HISTORY] agent_start event:', { agent_execution_id: msg.agent_execution_id, agent_name: content?.agent_name });
      allEvents.push({
        type: 'agent_start',
        execution_id: msg.agent_execution_id,
        messageId,
        timestamp,
        agent_name: (content?.agent_name as string) || '',
      });
    }
  }

  // Flush final assistant message
  flushAssistantMessage();

  console.log('🔄 Conversion complete - isLastExecutionActive:', lastExecutionIsActive);
  return { messages: threadMessages, metadata: metadataMap, events: allEvents, isLastExecutionActive: lastExecutionIsActive };
}

// Generate a simple UUID v4
function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

// Identity is now required - getIdentity() throws if setIdentity() wasn't called
// These values are retrieved at runtime when the provider mounts, not at module load time

interface ConversationContextValue {
  // Current conversation state
  conversation: ConversationResponse | null;
  conversationId: string | null;
  isLoading: boolean;
  error: string | null;

  // Conversation list (for sidebar)
  conversations: ConversationResponse[];
  loadConversations: (mode?: LifecycleMode | null, agentType?: string) => Promise<void>;

  // Identity context (for multi-tenancy)
  tenantId: string;
  projectId: string;
  userId: string;

  // Actions
  startNewConversation: (options?: {
    goal?: string;
    title?: string;
    mode?: LifecycleMode;
    agentType?: string;
    agentName?: string;
    agentDefinition?: PlatformAgentDefinition;
  }) => Promise<ConversationResponse | PlatformConversationResponse>;
  ensureConversation: (goal?: string) => Promise<string>;
  updateConversation: (updates: { title?: string; goal?: string; tags?: string[] }) => Promise<void>;
  updateConversationTitle: (conversationId: string, newTitle: string) => Promise<void>;
  deleteConversation: (conversationId: string) => Promise<void>;
  refreshConversation: () => Promise<void>;
  clearConversation: () => void;
  switchToConversation: (conversationId: string) => Promise<ConversationResponse | null>;
  fetchMessages: (conversationId: string) => Promise<ConversionResult>;

  // Execution tracking
  addExecution: (executionId: string, agentType: string) => void;
  executionCount: number;
}

const ConversationContext = createContext<ConversationContextValue | null>(null);

interface ConversationProviderProps {
  children: React.ReactNode;
  // Optional overrides for multi-tenancy
  tenantId?: string;
  projectId?: string;
  userId?: string;
}

export function ConversationProvider({
  children,
  tenantId,
  projectId,
  userId,
}: ConversationProviderProps) {
  // Get identity from runtime config - throws if setIdentity() wasn't called
  const identity = getIdentity();

  // Use provided values or fall back to identity values
  const effectiveTenantId = tenantId ?? identity.tenantId;
  const effectiveProjectId = projectId ?? identity.projectId;
  const effectiveUserId = userId ?? identity.userId;
  const [conversation, setConversation] = useState<ConversationResponse | null>(null);
  const [conversations, setConversations] = useState<ConversationResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track executions locally (also tracked in backend)
  const executionCountRef = useRef(0);
  const [executionCount, setExecutionCount] = useState(0);

  // Ref to prevent duplicate conversation creation
  const creatingConversationRef = useRef(false);
  const conversationPromiseRef = useRef<Promise<ConversationResponse | PlatformConversationResponse> | null>(null);

  /**
   * Load conversations from backend
   * Gracefully handles missing endpoint (conversation API may not be deployed)
   * Preserves local-only conversations when merging with API results
   * @param mode - Optional mode filter (null = all conversations)
   * @param agentType - Optional agent_type filter for filtering by specific agent
   */
  const loadConversations = useCallback(async (mode?: LifecycleMode | null, agentType?: string): Promise<void> => {
    try {
      const response = await apiClient.listConversations({
        tenant_id: effectiveTenantId,
        project_id: effectiveProjectId,
        mode: mode || undefined,  // Pass mode filter if provided
        agent_type: agentType,  // Pass agent_type filter if provided
        page_size: 50,
      });
      // Preserve local conversations (those with local- prefix)
      // When filtering by mode, also filter local conversations
      setConversations(prev => {
        const localConvs = prev.filter(c => {
          if (!c.conversation_id.startsWith('local-')) return false;
          // If mode filter is active, filter local conversations too
          if (mode) {
            const localMode = c.metadata?.mode as string;
            return localMode === mode;
          }
          return true;
        });
        // Merge: local conversations first, then API conversations
        const apiConvIds = new Set(response.conversations.map(c => c.conversation_id));
        const uniqueLocalConvs = localConvs.filter(c => !apiConvIds.has(c.conversation_id));
        return [...uniqueLocalConvs, ...response.conversations];
      });
      console.log('📋 Loaded conversations:', response.conversations.length, 'mode:', mode || 'all');
    } catch (err) {
      // Gracefully handle - conversation API may not be available
      // Keep existing local conversations
      console.warn('⚠️ Conversation API not available:', err);
      setConversations(prev => prev.filter(c => c.conversation_id.startsWith('local-')));
    }
  }, [effectiveTenantId, effectiveProjectId]);

  /**
   * Switch to an existing conversation
   * Just updates conversation state - messages are fetched by component on mount
   * Also fetches messages to count unique executions (backend doesn't track this)
   * Returns the conversation object for caller to read metadata (e.g., mode)
   */
  const switchToConversation = useCallback(async (convId: string): Promise<ConversationResponse | null> => {
    console.log('🔄 switchToConversation - convId:', convId);

    // Get conversation details and set state
    try {
      const conv = await apiClient.getConversation(convId, effectiveTenantId);

      // Backend doesn't track execution_count, so count from messages
      let execCount = conv.execution_count || 0;
      if (execCount === 0) {
        try {
          const msgs = await apiClient.getConversationMessages(convId, effectiveTenantId);
          // Count unique agent_execution_ids
          const uniqueExecs = new Set(msgs.messages.map(m => m.agent_execution_id));
          execCount = uniqueExecs.size;
        } catch {
          // Ignore message fetch errors
        }
      }

      const updatedConv = { ...conv, execution_count: execCount };
      setConversation(updatedConv);
      executionCountRef.current = execCount;
      setExecutionCount(execCount);

      // Also update the conversations list with the count
      setConversations(prev =>
        prev.map(c =>
          c.conversation_id === convId
            ? { ...c, execution_count: execCount }
            : c
        )
      );

      return updatedConv;
    } catch (err) {
      console.warn('⚠️ API unavailable, using minimal conversation:', err);
      const minimalConv: ConversationResponse = {
        conversation_id: convId,
        tenant_id: effectiveTenantId,
        project_id: effectiveProjectId,
        user_id: effectiveUserId,
        status: 'active' as const,
        execution_count: 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        tags: [],
        metadata: {},
      };
      setConversation(minimalConv);
      return minimalConv;
    }
  }, [effectiveTenantId, effectiveProjectId, effectiveUserId]);

  /**
   * Fetch messages for a conversation from backend
   * Called by component on mount
   * Returns messages, metadata map, and execution events for display
   */
  const fetchMessages = useCallback(async (convId: string): Promise<ConversionResult> => {
    console.log('🔍 fetchMessages called for convId:', convId, 'tenantId:', effectiveTenantId);
    try {
      const allMessages: ConversationMessage[] = [];
      let page = 1;
      let hasMore = true;

      // Fetch all pages
      while (hasMore) {
        console.log(`🔍 Fetching page ${page}...`);
        const response = await apiClient.getConversationMessages(convId, effectiveTenantId, page, 1000);
        allMessages.push(...response.messages);
        hasMore = response.has_more;
        console.log(`📄 Page ${page}: ${response.messages.length} messages, has_more: ${hasMore}`);
        page++;

        // Safety limit to prevent infinite loops
        if (page > 100) {
          console.warn('⚠️ Reached page limit of 100, stopping pagination');
          break;
        }
      }

      console.log('🔍 Backend response - total message count:', allMessages.length);

      if (allMessages.length > 0) {
        const result = convertMessagesToThreadFormat(allMessages);
        console.log('📥 Converted threadMessages:', result.messages.length, 'metadata entries:', result.metadata.size, 'events:', result.events.length);
        return result;
      }
    } catch (err) {
      console.warn('⚠️ Failed to fetch messages:', err);
    }
    return { messages: [], metadata: new Map(), events: [], isLastExecutionActive: false };
  }, [effectiveTenantId]);

  /**
   * Start a new conversation (exploration session)
   * Supports mode-specific title, metadata, and specific agent type.
   *
   * If agentDefinition is provided, creates a platform conversation with inline
   * agent definition. Otherwise, creates a regular conversation.
   */
  const startNewConversation = useCallback(async (
    options?: {
      goal?: string;
      title?: string;
      mode?: LifecycleMode;
      agentType?: string;
      agentName?: string;
      agentDefinition?: PlatformAgentDefinition;
    }
  ): Promise<ConversationResponse | PlatformConversationResponse> => {
    const { goal, title, mode, agentType, agentName, agentDefinition } = options || {};
    setIsLoading(true);
    setError(null);

    try {
      // Use provided title, or generate from goal, or use default
      const autoTitle = title
        ? title
        : goal
          ? (goal.length > 50 ? goal.slice(0, 50) + '...' : goal)
          : `Chat ${new Date().toLocaleDateString()}`;

      // If agentDefinition is provided, use platform conversation route
      if (agentDefinition) {
        const platformResponse = await apiClient.createPlatformConversation({
          agent_definition: agentDefinition,
          goal: goal || '',
          tenant_id: effectiveTenantId,
          project_id: effectiveProjectId,
          user_id: effectiveUserId,
        });

        // Convert PlatformConversationResponse to ConversationResponse-like for internal state
        const conversationLike: ConversationResponse = {
          conversation_id: platformResponse.conversation_id,
          tenant_id: effectiveTenantId,
          project_id: effectiveProjectId,
          user_id: effectiveUserId,
          title: autoTitle,
          goal: goal,
          status: 'active',
          tags: mode ? [mode] : [],
          metadata: {
            source: 'eureka-agent-interface',
            created_via: 'platform_agent',
            mode: mode || 'explore',
            platform_agent: true,
            agent_definition_id: platformResponse.agent_definition_id,
            agent_version_id: platformResponse.agent_version_id,
            initial_execution_id: platformResponse.execution_id,
            ...(agentName && { agent_name: agentName }),
          },
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };

        setConversation(conversationLike);
        setConversations(prev => [conversationLike, ...prev.filter(c => c.conversation_id !== conversationLike.conversation_id)]);
        executionCountRef.current = 1; // Platform conversation creates initial execution
        setExecutionCount(1);

        console.log('📝 Platform conversation created:', platformResponse.conversation_id,
          'agent:', agentDefinition.name, 'v' + agentDefinition.version,
          'execution:', platformResponse.execution_id);

        return platformResponse;
      }

      // Standard conversation creation (existing flow)
      const newConversation = await apiClient.createConversation({
        tenant_id: effectiveTenantId,
        project_id: effectiveProjectId,
        user_id: effectiveUserId,
        title: autoTitle,
        goal,
        tags: mode ? [mode] : [],
        // Pass agent_type as top-level field for API filtering
        ...(agentType && { agent_type: agentType }),
        metadata: {
          source: 'eureka-agent-interface',
          created_via: 'chat',
          mode: mode || 'explore',
          // Also store in metadata for backward compatibility
          ...(agentType && { agent_type: agentType }),
          // Store agent display name for header
          ...(agentName && { agent_name: agentName }),
        },
      });

      setConversation(newConversation);
      // Add to conversations list for sidebar
      setConversations(prev => [newConversation, ...prev.filter(c => c.conversation_id !== newConversation.conversation_id)]);
      executionCountRef.current = 0;
      setExecutionCount(0);

      console.log('📝 New conversation created:', newConversation.conversation_id, 'mode:', mode, 'agentType:', agentType, 'agentName:', agentName);
      return newConversation;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create conversation';
      setError(message);
      console.error('❌ Failed to create conversation:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [effectiveTenantId, effectiveProjectId, effectiveUserId]);

  /**
   * Ensure a conversation exists, creating one if needed
   * This is the main method to call before starting an agent
   * Uses locking to prevent duplicate creation
   * Falls back to local conversation when API unavailable
   */
  const ensureConversation = useCallback(async (goal?: string): Promise<string> => {
    // If we already have a conversation, return its ID
    if (conversation) {
      return conversation.conversation_id;
    }

    // If we're already creating a conversation, wait for it
    if (creatingConversationRef.current && conversationPromiseRef.current) {
      try {
        const existing = await conversationPromiseRef.current;
        return existing.conversation_id;
      } catch {
        // Previous creation failed, try again
      }
    }

    // Create a new conversation
    creatingConversationRef.current = true;
    conversationPromiseRef.current = startNewConversation({ goal });

    try {
      const newConversation = await conversationPromiseRef.current;
      return newConversation.conversation_id;
    } catch (err) {
      // API failed, create a local-only conversation for caching
      console.warn('⚠️ Conversation API unavailable, using local conversation');
      const localId = `local-${generateUUID()}`;
      const localTitle = goal
        ? (goal.length > 50 ? goal.slice(0, 50) + '...' : goal)
        : `Chat ${new Date().toLocaleDateString()}`;
      const localConversation: ConversationResponse = {
        conversation_id: localId,
        tenant_id: effectiveTenantId,
        project_id: effectiveProjectId,
        user_id: effectiveUserId,
        title: localTitle,
        goal,
        status: 'active',
        execution_count: 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        tags: [],
        metadata: {},
      };
      setConversation(localConversation);
      // Add to local conversations list for sidebar
      setConversations(prev => [localConversation, ...prev]);
      return localId;
    } finally {
      creatingConversationRef.current = false;
      conversationPromiseRef.current = null;
    }
  }, [conversation, startNewConversation, effectiveTenantId, effectiveProjectId, effectiveUserId]);

  /**
   * Update the current conversation
   */
  const updateConversation = useCallback(async (updates: {
    title?: string;
    goal?: string;
    tags?: string[];
  }): Promise<void> => {
    if (!conversation) {
      console.warn('No conversation to update');
      return;
    }

    try {
      const updated = await apiClient.updateConversation(
        conversation.conversation_id,
        effectiveTenantId,
        updates
      );
      setConversation(updated);
      console.log('📝 Conversation updated:', updated.conversation_id);
    } catch (err) {
      console.error('❌ Failed to update conversation:', err);
      throw err;
    }
  }, [conversation, effectiveTenantId]);

  /**
   * Update conversation title by ID
   * Can update any conversation, not just the current one
   */
  const updateConversationTitle = useCallback(async (
    conversationId: string,
    newTitle: string
  ): Promise<void> => {
    try {
      await apiClient.updateConversation(
        conversationId,
        effectiveTenantId,
        { title: newTitle }
      );

      // Update local state if it's the current conversation
      if (conversation?.conversation_id === conversationId) {
        setConversation(prev => prev ? { ...prev, title: newTitle } : prev);
      }

      // Update the conversations list
      setConversations(prev =>
        prev.map(c =>
          c.conversation_id === conversationId
            ? { ...c, title: newTitle }
            : c
        )
      );

      console.log('📝 Conversation title updated:', conversationId, newTitle);
    } catch (err) {
      console.error('❌ Failed to update conversation title:', err);
      throw err;
    }
  }, [conversation, effectiveTenantId]);

  /**
   * Delete a conversation by ID
   * Removes from backend and local state
   */
  const deleteConversation = useCallback(async (
    conversationId: string
  ): Promise<void> => {
    try {
      await apiClient.deleteConversation(conversationId, effectiveTenantId);

      // Clear current conversation if it's the one being deleted
      if (conversation?.conversation_id === conversationId) {
        setConversation(null);
        executionCountRef.current = 0;
        setExecutionCount(0);
      }

      // Remove from conversations list
      setConversations(prev =>
        prev.filter(c => c.conversation_id !== conversationId)
      );

      console.log('🗑️ Conversation deleted:', conversationId);
    } catch (err) {
      console.error('❌ Failed to delete conversation:', err);
      throw err;
    }
  }, [conversation, effectiveTenantId]);

  /**
   * Refresh conversation data from backend
   */
  const refreshConversation = useCallback(async (): Promise<void> => {
    if (!conversation) return;

    try {
      const refreshed = await apiClient.getConversation(
        conversation.conversation_id,
        effectiveTenantId
      );
      setConversation(refreshed);
      if (refreshed.execution_count !== undefined) {
        executionCountRef.current = refreshed.execution_count;
        setExecutionCount(refreshed.execution_count);
      }
    } catch (err) {
      console.error('❌ Failed to refresh conversation:', err);
    }
  }, [conversation, effectiveTenantId]);

  /**
   * Clear the current conversation (start fresh)
   */
  const clearConversation = useCallback(() => {
    setConversation(null);
    setError(null);
    executionCountRef.current = 0;
    setExecutionCount(0);
    console.log('🗑️ Conversation cleared');
  }, []);

  /**
   * Track a new execution in this conversation
   * Updates local state AND the conversations list for sidebar
   */
  const addExecution = useCallback((executionId: string, agentType: string) => {
    executionCountRef.current += 1;
    setExecutionCount(executionCountRef.current);

    // Also update the conversation in the list (for sidebar)
    if (conversation) {
      const newCount = executionCountRef.current;
      setConversations(prev =>
        prev.map(c =>
          c.conversation_id === conversation.conversation_id
            ? { ...c, execution_count: newCount }
            : c
        )
      );
    }

    console.log(`📊 Execution tracked: ${executionId} (${agentType}) - Total: ${executionCountRef.current}`);
  }, [conversation]);

  const value: ConversationContextValue = {
    conversation,
    conversationId: conversation?.conversation_id || null,
    isLoading,
    error,
    conversations,
    loadConversations,
    tenantId: effectiveTenantId,
    projectId: effectiveProjectId,
    userId: effectiveUserId,
    startNewConversation,
    ensureConversation,
    updateConversation,
    updateConversationTitle,
    deleteConversation,
    refreshConversation,
    clearConversation,
    switchToConversation,
    fetchMessages,
    addExecution,
    executionCount,
  };

  return (
    <ConversationContext.Provider value={value}>
      {children}
    </ConversationContext.Provider>
  );
}

/**
 * Hook to access conversation context
 */
export function useConversation(): ConversationContextValue {
  const context = useContext(ConversationContext);
  if (!context) {
    throw new Error('useConversation must be used within a ConversationProvider');
  }
  return context;
}

/**
 * Hook to get conversation ID (creates one if needed)
 * Useful for components that need to ensure a conversation exists
 */
export function useConversationId(): {
  conversationId: string | null;
  ensureConversationId: (goal?: string) => Promise<string>;
  isLoading: boolean;
} {
  const { conversationId, ensureConversation, isLoading } = useConversation();

  return {
    conversationId,
    ensureConversationId: ensureConversation,
    isLoading,
  };
}
